/**
 * TeamSection (Phase 2.7) — admin-only inline editor for a doctor's team.
 *
 * Modal-shaped surface opened from ManageStaffPage's "Team" button on
 * doctor rows. Calls /doctor-teams endpoints via doctorTeamStore.
 *
 * Behavior:
 *   - Admin lists every clinic staff member on the right; team members
 *     on the left. Click a staff row to add to / remove from the team.
 *   - Each team row gets a single label input ("Primary nurse",
 *     "Backup approver", ...). Empty label is fine.
 *   - "Save" sends the COMPLETE member list — backend diffs to emit
 *     focused audit events.
 *   - Removing a member with active pre-execute sittings flips those
 *     sittings to needs_reassignment server-side; the UI shows a
 *     short note about that (no need to ask twice).
 *
 * Permission: rendered only when `isAdmin` — caller (ManageStaffPage)
 * shouldn't surface the launcher to non-admins. Defensive `return null`
 * for non-admins as a safety net.
 *
 * Day-mode contrast + button-visibility rules from CLAUDE.md applied.
 */
import React, { useEffect, useMemo, useState } from 'react';

import useDoctorTeamStore from '../../stores/doctorTeamStore';


export default function TeamSection({
  open,
  onClose,
  doctor,         // {member_id, first_name, last_name, ...}
  staff = [],     // full clinic roster — caller passes this
  isAdmin,
}) {
  const teamsByDoctor = useDoctorTeamStore((s) => s.teamsByDoctor);
  const fetchTeam = useDoctorTeamStore((s) => s.fetchTeam);
  const upsertTeam = useDoctorTeamStore((s) => s.upsertTeam);
  const deleteTeamRow = useDoctorTeamStore((s) => s.deleteTeam);

  const team = doctor ? teamsByDoctor[doctor.member_id] : null;

  // Local draft — array of {member_id, role, label}.
  const [draftMembers, setDraftMembers] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!open || !doctor) return;
    setErrorMsg(null);
    setReason('');
    fetchTeam(doctor.member_id).then(() => {
      // After fetch, hydrate draft from the latest store snapshot.
      const t = useDoctorTeamStore.getState().teamsByDoctor[doctor.member_id];
      setDraftMembers((t?.members || []).map((m) => ({
        member_id: m.member_id,
        role: m.role,
        label: m.label || '',
      })));
    });
  }, [open, doctor, fetchTeam]);

  const draftIds = useMemo(
    () => new Set(draftMembers.map((m) => m.member_id)),
    [draftMembers],
  );

  const eligibleStaff = useMemo(
    () => staff.filter((s) => s.member_id !== doctor?.member_id),
    [staff, doctor],
  );

  const addMember = (s) => {
    if (draftIds.has(s.member_id)) return;
    setDraftMembers([
      ...draftMembers,
      { member_id: s.member_id, role: s.role, label: '' },
    ]);
  };

  const removeMember = (memberId) => {
    setDraftMembers(draftMembers.filter((m) => m.member_id !== memberId));
  };

  const setLabel = (memberId, label) => {
    setDraftMembers(
      draftMembers.map((m) =>
        m.member_id === memberId ? { ...m, label } : m,
      ),
    );
  };

  const onSave = async () => {
    if (!doctor) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const body = {
        members: draftMembers.map((m) => ({
          member_id: m.member_id,
          role: m.role,
          ...(m.label?.trim() ? { label: m.label.trim() } : {}),
        })),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };
      const r = await upsertTeam(doctor.member_id, body);
      if (!r.success) {
        setErrorMsg(r.error || 'Save failed.');
        return;
      }
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const onClearTeam = async () => {
    if (!doctor) return;
    if (!window.confirm(
      `Clear ${doctor.first_name || 'this doctor'}'s team? `
      + 'In-flight pre-execute sittings assigned to former team members '
      + 'will flip to "Needs reassignment".',
    )) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const r = await deleteTeamRow(doctor.member_id);
      if (!r.success) {
        setErrorMsg(r.error || 'Could not clear team.');
        return;
      }
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  if (!open || !doctor) return null;
  if (!isAdmin) return null;

  const doctorName = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim()
    || doctor.member_id;

  const staffNameById = (mid) => {
    const s = staff.find((row) => row.member_id === mid);
    if (!s) return mid;
    return s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || mid;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-xl shadow-xl overflow-hidden text-slate-900 dark:text-slate-100">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/40">
          <div>
            <h2 className="text-base font-bold text-indigo-900 dark:text-indigo-100">
              Team for Dr. {doctorName}
            </h2>
            <p className="text-xs font-medium text-indigo-800 dark:text-indigo-200">
              Members listed here may be assigned to this doctor&apos;s plan
              steps. Default-open if cleared (any staff member is fair game).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5">
          {/* Current team */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase">
              Team ({draftMembers.length})
            </div>
            {draftMembers.length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-3 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                No team configured. Click a staff member on the right to add.
              </div>
            )}
            {draftMembers.map((m) => (
              <div
                key={m.member_id}
                className="flex flex-wrap items-center gap-2 p-2 rounded-md border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                    {staffNameById(m.member_id)}
                  </div>
                  <div className="text-xs text-slate-700 dark:text-slate-300">{m.role}</div>
                </div>
                <input
                  type="text"
                  value={m.label || ''}
                  onChange={(e) => setLabel(m.member_id, e.target.value)}
                  placeholder="Label (e.g. Primary nurse)"
                  className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 w-44"
                />
                <button
                  type="button"
                  onClick={() => removeMember(m.member_id)}
                  className="text-xs font-medium px-2 py-1 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* Eligible staff */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase">
              Add from staff
            </div>
            {eligibleStaff.length === 0 && (
              <div className="text-sm text-slate-600 dark:text-slate-300 italic py-3 text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-md">
                No other staff members in the org.
              </div>
            )}
            {eligibleStaff.map((s) => {
              const onTeam = draftIds.has(s.member_id);
              return (
                <button
                  key={s.member_id}
                  type="button"
                  onClick={() => addMember(s)}
                  disabled={onTeam}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60 text-left"
                >
                  <span className="truncate">
                    {s.name || s.member_id}
                    <span className="ml-2 text-xs text-slate-600 dark:text-slate-300">
                      {s.role}
                    </span>
                  </span>
                  <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-200 ml-2">
                    {onTeam ? 'On team' : '+ Add'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-5 pb-4 space-y-2">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Reason for change (optional, captured on the audit trail)
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full text-sm font-medium px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              placeholder="e.g. Nurse A on maternity leave"
            />
          </label>
          {errorMsg && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30">
          <button
            type="button"
            onClick={onClearTeam}
            disabled={busy || !team}
            className="text-xs font-medium px-3 py-1.5 rounded-md border border-red-400 dark:border-red-700 bg-white dark:bg-slate-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-60"
          >
            Clear team (default-open)
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="text-sm font-medium px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={busy}
              className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Save team'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
